import React from "react";
import styles from "./customizableOptions.module.css";

const CustomizableOptions = ({
  options,
  onOptionChange,
  customOptions,
}: {
  options: any[];
  onOptionChange: (id: string, value: any) => void;
  customOptions: { [key: string]: any };
}) => {
  const renderOption = (option: any) => {
    const uniqueOptionId = `${option.__component}-${option.id}`;
    const inputText = option.inputText ?? ""; // Ensure inputText is a string or empty
    const priceSurchargeText = option.priceSurcharge
      ? ` (+ S/ ${option.priceSurcharge.toFixed(2)})`
      : "";

    // Add a red asterisk for required fields
    const title = (
      <>
        {option.title}
        {priceSurchargeText}
        {option.required && (
          <span style={{ color: "red", marginRight: "0.3rem" }}> *</span>
        )}
      </>
    );

    switch (option.__component) {
      case "product-custom-field.text-short":
        const maxShortChars = 50; // Character limit for text-short
        const currentShortText = customOptions[uniqueOptionId]?.value || ""; // Use customOptions state directly

        return (
          <div key={uniqueOptionId} className={styles.option}>
            <label>{title}</label>
            <input
              type="text"
              maxLength={maxShortChars} // Limit input to 50 characters
              value={currentShortText}
              onChange={
                (e) => onOptionChange(uniqueOptionId, e.target.value) // Pass value directly without modification
              }
              placeholder="Escribe aquí..." // Optional: Add placeholder text
            />
            <div className={styles.charCounter}>
              {currentShortText.length}/{maxShortChars} Caracteres
            </div>
          </div>
        );

      case "product-custom-field.text-long":
        const maxChars = 200; // Character limit for text-long
        const currentText = customOptions[uniqueOptionId]?.value || ""; // Use customOptions state directly

        return (
          <div key={uniqueOptionId} className={styles.option}>
            <label>{title}</label>
            <textarea
              maxLength={maxChars} // Limit input to 500 characters
              value={currentText}
              onChange={
                (e) => onOptionChange(uniqueOptionId, e.target.value) // Pass value directly without modification
              }
              placeholder="Escribe aquí..." // Optional: Add placeholder text
            />
            <div className={styles.charCounter}>
              {currentText.length}/{maxChars} Caracteres
            </div>
          </div>
        );
      case "product-custom-field.single-option":
        if (!option.options || option.options.length === 0) {
          console.error(`Single-option "${option.title}" has no values.`);
          return null;
        }

        const singleOptionValue = customOptions[uniqueOptionId]?.value || ""; // Empty string if no value selected

        return (
          <div key={uniqueOptionId} className={styles.option}>
            <label className={styles.optionTitle}>
              {title}
              {singleOptionValue && (
                <span
                  className={styles.clearSelection}
                  onClick={() => onOptionChange(uniqueOptionId, "")} // Clear selection
                >
                  Borrar selección
                </span>
              )}
            </label>
            {option.options.map((value: any) => (
              <div key={value.id} className={styles.radioOption}>
                <input
                  type="radio"
                  name={`single-option-${uniqueOptionId}`} // Group radio buttons by unique ID
                  value={value.optionLabel}
                  checked={singleOptionValue === value.optionLabel} // Match state to selected value
                  onChange={() =>
                    onOptionChange(uniqueOptionId, value.optionLabel)
                  }
                />
                <>
                  {value.optionLabel}
                  {value.priceSurcharge
                    ? ` (+S/ ${value.priceSurcharge.toFixed(2)})`
                    : ""}
                </>
              </div>
            ))}
          </div>
        );

      case "product-custom-field.multi-select":
        const selectedValues = Array.isArray(
          customOptions[uniqueOptionId]?.value
        )
          ? customOptions[uniqueOptionId].value
          : []; // Ensure selectedValues is an array

        if (!option.options) {
          console.error(
            `Options for multi-select "${option.title}" are undefined.`
          );
          return null;
        }

        return (
          <div key={uniqueOptionId} className={styles.option}>
            <label>{title}</label>
            <div>
              {option.options.map((value: any) => (
                <div key={value.id} className={styles.checkboxOption}>
                  <input
                    type="checkbox"
                    checked={selectedValues.includes(value.optionLabel)} // Check state based on selectedValues
                    onChange={(e) => {
                      const newValues = e.target.checked
                        ? [...selectedValues, value.optionLabel] // Add selected option
                        : selectedValues.filter(
                            (v: any) => v !== value.optionLabel
                          ); // Remove deselected option
                      onOptionChange(uniqueOptionId, newValues); // Update customOptions state
                    }}
                  />
                  <>
                    {value.optionLabel}
                    {value.priceSurcharge
                      ? ` (+S/ ${value.priceSurcharge.toFixed(2)})`
                      : ""}
                  </>
                </div>
              ))}
            </div>
          </div>
        );

      case "product-custom-field.dropdown":
        if (!option.options || option.options.length === 0) {
          console.error(`Dropdown "${option.title}" has no values.`);
          return null;
        }

        const dropdownValue = customOptions[uniqueOptionId]?.value || ""; // Empty string if no value selected

        return (
          <div key={uniqueOptionId} className={styles.option}>
            <label className={styles.optionTitle}>
              {title}
              {dropdownValue && (
                <span
                  className={styles.clearSelection}
                  onClick={() => onOptionChange(uniqueOptionId, "")} // Clear selection
                >
                  Borrar selección
                </span>
              )}
            </label>
            <select
              value={dropdownValue} // Use dropdownValue, which defaults to ""
              onChange={(e) => onOptionChange(uniqueOptionId, e.target.value)}
            >
              <option value="" disabled>
                Escoge
              </option>
              {option.options.map((value: any) => (
                <option key={value.id} value={value.optionLabel}>
                  {value.optionLabel}
                  {value.priceSurcharge
                    ? ` (+S/ ${value.priceSurcharge.toFixed(2)})`
                    : ""}
                </option>
              ))}
            </select>
          </div>
        );

      default:
        console.error(`Unknown component type: ${option.__component}`);
        return null;
    }
  };

  return <div>{options.map(renderOption)}</div>;
};

export default CustomizableOptions;
